import { Close, Save } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import {
  PersonProfile,
  updatePersonProfile,
} from '~/data/profile/usePersonProfile';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';

export type SocialLinksForm = {
  socialProfile: string;
  empPresentation: string;
};

type PropsType = {
  personProfile: PersonProfile;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
};

export const SocialLinkEditDialog = (props: PropsType) => {
  const { personProfile, open, onClose, onUpdate } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const validationSchema = yup.object().shape<SocialLinksForm>({
    socialProfile: yup.string().url(t('laNotValidURL')).defined(),
    empPresentation: yup.string().url(t('laNotValidURL')).defined(),
  });

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const {
    control,
    formState: { isValid, isDirty, isSubmitting },
    reset,
    handleSubmit,
  } = useForm<SocialLinksForm>({
    defaultValues: {
      socialProfile: personProfile.socialProfile ?? '',
      empPresentation: personProfile.empPresentation ?? '',
    },
    resolver,
  });

  const resetForm = useCallback(() => {
    reset({
      socialProfile: personProfile.socialProfile ?? '',
      empPresentation: personProfile.empPresentation ?? '',
    });
  }, [reset, personProfile]);

  const profileHasChanged = useHasChanged(personProfile);
  useEffect(() => {
    let isMounted = true;

    if (isMounted && profileHasChanged) {
      resetForm();
    }

    return () => {
      isMounted = false;
    };
  }, [profileHasChanged, resetForm]);

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const payload: PersonProfile = { ...personProfile, ...data };

      return updatePersonProfile(personProfile.id, payload)
        .then(() => {
          onClose();
          onUpdate();
          enqueueSnackbar(t('change_saved'), { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar(t('laFailedToSave'), { variant: 'error' });
        });
    });
  }, [handleSubmit, onClose, onUpdate, enqueueSnackbar, personProfile, t]);

  return (
    <>
      <DraggableDialog open={open} onClose={handleCancel}>
        <SolaForceDialogTitle onClose={handleCancel}>
          {t('Links')}
        </SolaForceDialogTitle>
        <DialogContent dividers={true}>
          <Typography variant={'body2'}>{t('laAddFullURL')}</Typography>
          <Controller
            control={control}
            name={'socialProfile'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextField
                  value={value}
                  onChange={onChange}
                  label={t('laSocialProfile')}
                  autoFocus
                  margin="dense"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'empPresentation'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextField
                  value={value}
                  onChange={onChange}
                  label={t('laPresentation')}
                  margin="dense"
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Close />}
            disabled={isSubmitting}
            onClick={handleCancel}
            color="error"
          >
            {t('laClose')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting || !isValid || !isDirty}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {t('laSave')}
          </Button>
        </DialogActions>
      </DraggableDialog>
    </>
  );
};
